// src/utils/parseError.js

/**
 * حاول إيجاد أول رسالة نصية صالحة داخل كائن/مصفوفة (nested).
 * ترجع null لو ما فيه شيء واضح.
 */
function getFirstFromObject(obj) {
  if (!obj || typeof obj !== "object") return null;

  // ترتيب المفاتيح حسب الظهور
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (!val && val !== 0) continue;

    // لو كانت string
    if (typeof val === "string" && val.trim()) return val.trim();

    // لو كانت مصفوفة
    if (Array.isArray(val) && val.length) {
      // جرب تلاقي أول string صالحة داخلها أو nested objects
      for (const item of val) {
        if (!item && item !== 0) continue;
        if (typeof item === "string" && item.trim()) return item.trim();
        if (Array.isArray(item)) {
          const found = item.find((x) => typeof x === "string" && x.trim());
          if (found) return found.trim();
        }
        if (typeof item === "object") {
          const nested = getFirstFromObject(item);
          if (nested) return nested;
        }
      }
      // fallback: stringify أول عنصر لو كان شيء آخر
      try {
        return typeof val[0] === "string" ? val[0] : String(val[0]);
      } catch {
        // continue searching
      }
    }

    // لو كانت object نعمل بحث nested
    if (typeof val === "object") {
      const nested = getFirstFromObject(val);
      if (nested) return nested;
    }
  }

  return null;
}

/**
 * يستقبل respData (المحتوى داخل err.response.data عادةً)
 * ويرجع أول رسالة ممكنة من الحقول الشائعة مثل errors, error, message أو أي key آخر.
 */
export function extractFirstErrorMessage(respData) {
  if (!respData) return null;

  // مكان الأخطاء الشائع
  const candidates = [
    respData.errors ?? null,
    respData.error ?? null,
    respData.validation_errors ?? null, // لو backend يستخدم اسم مختلف
    respData.messages ?? null,
    respData // كـ fallback إذا كان respData نفسه array/object/string
  ];

  for (const cand of candidates) {
    if (!cand && cand !== 0) continue;

    // لو string مباشرة
    if (typeof cand === "string" && cand.trim()) return cand.trim();

    // لو array
    if (Array.isArray(cand) && cand.length) {
      for (const item of cand) {
        if (typeof item === "string" && item.trim()) return item.trim();
        if (typeof item === "object") {
          const nested = getFirstFromObject(item);
          if (nested) return nested;
        }
      }
      // fallback to first element
      try {
        return typeof cand[0] === "string" ? cand[0] : String(cand[0]);
      } catch {}
    }

    // لو object
    if (typeof cand === "object") {
      const first = getFirstFromObject(cand);
      if (first) return first;
    }
  }

  return null;
}

/**
 * الميزة: تستقبل كامل object الخطأ (مثلاً err من Axios)
 * وترجع رسالة جاهزة للعرض مع fallback افتراضي.
 */
export function getErrorMessageFromAxiosError(err, fallback = "Something went wrong!") {
  if (!err) return fallback;

  // عادةً Axios يحط الرد في err.response.data
  const respData = err?.response?.data ?? err?.response ?? err;

  const message = extractFirstErrorMessage(respData);
  return message || fallback;
}

export default getErrorMessageFromAxiosError;
